import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { differenceInCalendarDays, parseISO } from 'date-fns';

import TimePeriodSelect from '../Leader/TimePeriodSelect';
import { useFiscalTimePeriod } from '../../hooks/useFiscalTimePeriod';
import { useSfTeamOppActivities } from '../../hooks/useSfTeamOppActivities';
import {
  ACTIVITY_BUCKETS,
  ActivityBucket,
  OppActivityRow,
  OppActivitySortKey,
  activityTimeline,
  buildOppActivityRows,
  ownOpportunities,
  sortOppRows,
  summarizeOppActivity,
} from '../../support/profileMetrics';
import { demoOpportunities } from '../../support/profileMetricsDemo';
import { fmtDate, fmtShort, relativeDay as relative } from '../../support/dateLabels';
import { pageWindow } from '../../support/pageWindow';
import { useDemoFlag } from '../../support/useDemoFlag';
import './OpportunityActivity.scss';

const BUCKET_META: Record<ActivityBucket, { label: string; color: string }> = {
  demoPitch: { label: 'Demo / Pitch', color: '#3070e7' },
  design: { label: 'Customer Design', color: '#16bdeb' },
  pov: { label: 'POV', color: '#ff007f' },
  adoption: { label: 'Adoption', color: '#ff9000' },
  other: { label: 'Other', color: '#6b7280' },
};

const PAGE_SIZES = [5, 10, 25];
const PREVIEW_ITEMS = 6;

const COLUMNS: Array<{ key: OppActivitySortKey; label: string; numeric?: boolean }> = [
  { key: 'name', label: 'Opportunity' },
  { key: 'stage', label: 'Stage' },
  { key: 'closeDate', label: 'Close date' },
  { key: 'amount', label: 'Amount', numeric: true },
  { key: 'count', label: 'Activities' },
  { key: 'lastActivity', label: 'Last activity' },
];

const fmtMoney = (n: number | null) =>
  n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const STAGE_STEPS = 6;
const stageNumber = (stage: string): number | null => {
  const m = /^\s*(\d+)/.exec(stage);
  return m ? Math.min(STAGE_STEPS, Math.max(0, Number(m[1]))) : null;
};
const stageName = (stage: string) => stage.replace(/^\s*\d+\s*[-–:.]?\s*/, '') || stage;

const closeNote = (iso: string | null): string => {
  if (!iso) return '';
  const n = differenceInCalendarDays(parseISO(iso), new Date());
  return n < 0 ? (n === -1 ? '1 day overdue' : `${-n} days overdue`) : relative(iso);
};
const closeTone = (iso: string | null): string => {
  if (!iso) return '';
  const n = differenceInCalendarDays(parseISO(iso), new Date());
  return n < 0 ? 'is-late' : n <= 30 ? 'is-soon' : '';
};

// Avatars use the four spectrum colors as a light tint behind dark initials.
const AVATARS = ['#3070e7', '#16bdeb', '#ff007f', '#ff9000'];
const avatarFor = (label: string) => {
  const words = label.split(/\s+/).filter(Boolean);
  const initials = (words[0]?.[0] || '?') + (words[1]?.[0] || '');
  const hash = Array.from(label).reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
  return { initials: initials.toUpperCase(), bg: `${AVATARS[hash % AVATARS.length]}26` };
};

const mixText = (mix: Record<ActivityBucket, number>) =>
  ACTIVITY_BUCKETS.filter((b) => mix[b] > 0)
    .map((b) => `${BUCKET_META[b].label} ${mix[b]}`)
    .join(' · ');

const MixBar: React.FC<{ mix: Record<ActivityBucket, number> }> = ({ mix }) => (
  <span className="oa__mix" title={mixText(mix)} role="img" aria-label={mixText(mix)}>
    {ACTIVITY_BUCKETS.filter((b) => mix[b] > 0).map((b) => (
      <span key={b} style={{ flexGrow: mix[b], background: BUCKET_META[b].color }} />
    ))}
  </span>
);

interface Props {
  email: string | null | undefined;
}

const OpportunityActivity: React.FC<Props> = ({ email }) => {
  const navigate = useNavigate();
  // Sample data for previewing the layout: `?demo=activity` (or `all`) on a non-production build only.
  const demo = useDemoFlag('activity');
  const time = useFiscalTimePeriod(demo ? 'all' : 'quarter');
  const live = useSfTeamOppActivities(email, { enabled: !!email && !demo });

  const [query, setQuery] = useState('');
  const [types, setTypes] = useState<Set<ActivityBucket>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [hover, setHover] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<OppActivitySortKey>('lastActivity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);

  const opps = useMemo(() => (demo ? demoOpportunities() : ownOpportunities(live.data, email)), [demo, live.data, email]);
  // Type counts for the chips ignore the type filter itself, so the chips never disappear while selected.
  const base = useMemo(() => buildOppActivityRows(opps, time.range, { query }), [opps, time.range, query]);
  const chipCounts = useMemo(() => summarizeOppActivity(base.rows).mix, [base.rows]);
  const { rows, idle } = useMemo(
    () => (types.size ? buildOppActivityRows(opps, time.range, { query, types }) : base),
    [opps, time.range, query, types, base],
  );
  const summary = useMemo(() => summarizeOppActivity(rows), [rows]);
  const timeline = useMemo(() => activityTimeline(rows, time.range), [rows, time.range]);
  const sorted = useMemo(() => sortOppRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => setPage(0), [time.range, query, types, sortKey, sortDir, pageSize]);
  const current = Math.min(page, pages - 1);
  const visible = sorted.slice(current * pageSize, current * pageSize + pageSize);

  const sortBy = (key: OppActivitySortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'stage' ? 'asc' : 'desc');
    }
  };
  const toggleType = (b: ActivityBucket) =>
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      return next;
    });
  const toggleRow = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const loading = !demo && (live.isLoading || time.pending);
  const isError = !demo && live.isError;
  const from = sorted.length ? current * pageSize + 1 : 0;
  const to = Math.min(sorted.length, (current + 1) * pageSize);
  const filtered = types.size > 0 || query.trim() !== '';
  const peak = Math.max(1, ...timeline.map((b) => b.total));
  const hovered = hover != null ? timeline[hover] : null;
  const unit = timeline.length && timeline[0].detail.startsWith('Week') ? 'week' : 'month';

  return (
    <section className="v2__section oa">
      <div className="v2__wrap">
        <header className="oa__head">
          <div>
            <h3 className="oa__title">SE Activity by Opportunity</h3>
            <p className="oa__lede">
              The activities you logged on each opportunity you are on the team for.
              {demo && (
                <>
                  {' '}
                  <strong className="oa__demo">Showing sample data.</strong>
                </>
              )}
            </p>
          </div>
          <TimePeriodSelect
            periodId={time.periodId}
            onChange={time.setPeriodId}
            periods={time.periods}
            loading={time.pending}
            notice={time.notice}
          />
        </header>

        <div className="oa__summary">
          <dl className="oa__figures">
            <div>
              <dt>Opportunities</dt>
              <dd>{loading ? '—' : summary.opportunities}</dd>
            </div>
            <div>
              <dt>Activities</dt>
              <dd>{loading ? '—' : summary.activities}</dd>
            </div>
            <div>
              <dt>Most recent</dt>
              <dd className="oa__figure-date">{loading ? '—' : fmtDate(summary.lastActivity)}</dd>
            </div>
          </dl>

          {!loading && timeline.length > 1 && summary.activities > 0 && (
            <figure className="oa__chart" onMouseLeave={() => setHover(null)}>
              <figcaption aria-live="polite">
                {hovered ? (
                  <>
                    <b>{hovered.detail}</b>
                    <span>
                      {hovered.total} {hovered.total === 1 ? 'activity' : 'activities'}
                      {hovered.total > 0 && ` — ${mixText(hovered.mix)}`}
                    </span>
                  </>
                ) : (
                  <span>
                    Activity by {unit}, {timeline.length} {unit}s
                  </span>
                )}
              </figcaption>
              <div className="oa__bars">
                <span className="oa__peak">{peak}</span>
                {timeline.map((b, i) => (
                  <button
                    key={b.key}
                    type="button"
                    className={`oa__bar${hover === i ? ' is-hover' : ''}${b.total === 0 ? ' is-empty' : ''}`}
                    aria-label={`${b.detail}: ${b.total}`}
                    onMouseEnter={() => setHover(i)}
                    onFocus={() => setHover(i)}
                    onBlur={() => setHover(null)}
                  >
                    <span className="oa__stack" style={{ height: `${Math.max(b.total ? 8 : 2, (b.total / peak) * 100)}%` }}>
                      {ACTIVITY_BUCKETS.filter((t) => b.mix[t] > 0).map((t) => (
                        <i key={t} style={{ flexGrow: b.mix[t], background: BUCKET_META[t].color }} />
                      ))}
                    </span>
                  </button>
                ))}
              </div>
              <div className="oa__axis">
                <span>{timeline[0].label}</span>
                <span>{timeline[timeline.length - 1].label}</span>
              </div>
            </figure>
          )}
        </div>

        <div className="oa__card">
        <div className="oa__tools">
          <div className="oa__chips" role="group" aria-label="Filter by activity type">
            {ACTIVITY_BUCKETS.filter((b) => chipCounts[b] > 0 || types.has(b)).map((b) => (
              <button
                key={b}
                type="button"
                aria-pressed={types.has(b)}
                className={types.has(b) ? 'is-on' : ''}
                style={types.has(b) ? { borderColor: BUCKET_META[b].color, background: `${BUCKET_META[b].color}1f` } : undefined}
                onClick={() => toggleType(b)}
              >
                <i style={{ background: BUCKET_META[b].color }} />
                {BUCKET_META[b].label}
                <b>{chipCounts[b]}</b>
              </button>
            ))}
            {types.size > 0 && (
              <button type="button" className="oa__clear" onClick={() => setTypes(new Set())}>
                Clear
              </button>
            )}
          </div>
          <input
            type="search"
            className="oa__search"
            placeholder="Search opportunities"
            aria-label="Search opportunities"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="oa__table-wrap">
          <table className="oa__table">
            <thead>
              <tr>
                <th className="oa__toggle-col">
                  <span className="oa__sr">Show activities</span>
                </th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={c.numeric ? 'is-num' : undefined}
                    aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button type="button" onClick={() => sortBy(c.key)} className={sortKey === c.key ? `is-sorted is-${sortDir}` : ''}>
                      {c.label}
                    </button>
                  </th>
                ))}
                <th className="oa__go-col">
                  <span className="oa__sr">Open opportunity</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                [0, 1, 2, 3].map((i) => (
                  <tr key={i} className="oa__skeleton" aria-hidden="true">
                    <td colSpan={COLUMNS.length + 2}>
                      <span />
                    </td>
                  </tr>
                ))}
              {!loading && isError && (
                <tr>
                  <td colSpan={COLUMNS.length + 2} className="oa__state">
                    Couldn&apos;t load your opportunity activity.{' '}
                    <button type="button" className="oa__link" onClick={() => live.refetch()}>
                      Try again
                    </button>
                  </td>
                </tr>
              )}
              {!loading && !isError && sorted.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 2} className="oa__state">
                    {filtered
                      ? 'Nothing matches those filters.'
                      : opps.length === 0
                        ? 'You are not on the team for any opportunities yet.'
                        : 'No SE activity was logged on your opportunities in this period.'}
                  </td>
                </tr>
              )}
              {!loading &&
                !isError &&
                visible.map((r: OppActivityRow) => {
                  const open = expanded.has(r.id);
                  return (
                    <React.Fragment key={r.id}>
                      <tr className={`oa__row${open ? ' is-open' : ''}`} onClick={() => navigate(`/opportunity/${r.id}`)}>
                        <td className="oa__toggle-col">
                          <button
                            type="button"
                            className="oa__chev"
                            aria-expanded={open}
                            aria-label={`${open ? 'Hide' : 'Show'} activities for ${r.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(r.id);
                            }}
                          />
                        </td>
                        <td className="oa__opp">
                          <span className="oa__avatar" style={{ background: avatarFor(r.account || r.name).bg }} aria-hidden="true">
                            {avatarFor(r.account || r.name).initials}
                          </span>
                          <span className="oa__opp-text">
                            <Link to={`/opportunity/${r.id}`} onClick={(e) => e.stopPropagation()}>
                              {r.name}
                            </Link>
                            {r.account && <span>{r.account}</span>}
                          </span>
                        </td>
                        <td>
                          {r.stage ? (
                            <span className="oa__stage" title={r.stage}>
                              {stageNumber(r.stage) != null && (
                                <span className="oa__pips" aria-hidden="true">
                                  {Array.from({ length: STAGE_STEPS }, (_, i) => (
                                    <i key={i} className={i < (stageNumber(r.stage) as number) ? 'on' : ''} />
                                  ))}
                                </span>
                              )}
                              <span>{stageName(r.stage)}</span>
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <span className="oa__two">
                            {fmtDate(r.closeDate)}
                            {r.closeDate && <small className={closeTone(r.closeDate)}>{closeNote(r.closeDate)}</small>}
                          </span>
                        </td>
                        <td className="is-num oa__amount">{fmtMoney(r.amount)}</td>
                        <td>
                          <span className="oa__count">
                            <b>{r.count}</b>
                            <MixBar mix={r.mix} />
                          </span>
                        </td>
                        <td>
                          <span className="oa__two">
                            {fmtDate(r.lastActivity)}
                            <small>{relative(r.lastActivity)}</small>
                          </span>
                        </td>
                        <td className="oa__go-col" aria-hidden="true">
                          <span className="oa__go">&rarr;</span>
                        </td>
                      </tr>
                      {open && (
                        <tr className="oa__detail">
                          <td />
                          <td colSpan={COLUMNS.length + 1}>
                            <ol>
                              {r.activities.slice(0, PREVIEW_ITEMS).map((a) => (
                                <li key={a.id}>
                                  <time>{fmtShort(a.date)}</time>
                                  <i style={{ background: BUCKET_META[a.bucket].color }} title={BUCKET_META[a.bucket].label} />
                                  <span className="oa__subject">{a.subject}</span>
                                  <em>{BUCKET_META[a.bucket].label}</em>
                                  {a.status && <small>{a.status}</small>}
                                </li>
                              ))}
                            </ol>
                            {r.activities.length > PREVIEW_ITEMS && (
                              <Link className="oa__more" to={`/opportunity/${r.id}`}>
                                {r.activities.length - PREVIEW_ITEMS} more on the opportunity
                              </Link>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>

        {!loading && !isError && sorted.length > 0 && (
          <footer className="oa__foot">
            <span className="oa__range">
              {from}–{to} of {sorted.length}
              {!filtered && idle > 0 && <em> · {idle} more with no activity in this period</em>}
            </span>
            <div className="oa__pager">
              <label>
                Rows
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              {pages > 1 && (
                <nav aria-label="Pagination">
                  <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)}>
                    Previous
                  </button>
                  {pageWindow(current, pages).map((p, i) =>
                    p === 'gap' ? (
                      <span key={`g${i}`} className="oa__gap">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        className={p === current ? 'is-current' : ''}
                        aria-current={p === current ? 'page' : undefined}
                        onClick={() => setPage(p)}
                      >
                        {p + 1}
                      </button>
                    ),
                  )}
                  <button type="button" disabled={current === pages - 1} onClick={() => setPage(current + 1)}>
                    Next
                  </button>
                </nav>
              )}
            </div>
          </footer>
        )}
        </div>
      </div>
    </section>
  );
};

export default OpportunityActivity;
